import React, { useState } from 'react';

interface ValidatedSelectProps {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  showValidation?: boolean;
  serverErrors?: string[];
  hint?: string;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}

const ValidatedSelect: React.FC<ValidatedSelectProps> = ({
  id,
  name,
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  className = '',
  showValidation = true,
  serverErrors = [],
  hint,
  options,
  placeholder,
}) => {
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [hasServerErrors, setHasServerErrors] = useState(false);

  // Track when server errors are received
  React.useEffect(() => {
    if (serverErrors && serverErrors.length > 0) {
      setHasServerErrors(true);
    }
  }, [serverErrors]);

  const isEmpty = value.length === 0;

  // Red border only when server errors exist and user hasn't changed field
  const hasServerValidationErrors =
    hasServerErrors && serverErrors && serverErrors.length > 0;

  // Show hint when field is focused, invalid, or has server errors
  const shouldShowHint = hasBeenFocused || hasServerValidationErrors;

  let hintMessage = '';
  let isHintRed = false;

  if (shouldShowHint) {
    // Prioritize server errors if they exist
    if (hasServerValidationErrors) {
      hintMessage = serverErrors[0];
      isHintRed = true;
    } else if (hasBeenFocused && required && isEmpty) {
      hintMessage = hint || `${label} is required.`;
      isHintRed = false;
    } else if (hasBeenFocused && hint) {
      hintMessage = hint;
      isHintRed = false;
    }
  }

  // Handle input change - clear server error state when user changes
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setHasServerErrors(false);
    onChange(e);
  };

  return (
    <div className={className}>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div>
        <select
          id={id}
          name={name}
          value={value}
          onChange={handleChange}
          onFocus={() => setHasBeenFocused(true)}
          required={required}
          disabled={disabled}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
            hasServerValidationErrors
              ? 'border-red-500 dark:border-red-500'
              : 'border-gray-300 dark:border-gray-600'
          }`}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {showValidation && shouldShowHint && hintMessage && (
        <p
          className={`mt-1 text-xs ${
            isHintRed
              ? 'text-red-600 dark:text-red-400'
              : 'text-gray-600 dark:text-gray-400'
          }`}
        >
          {hintMessage}
        </p>
      )}
    </div>
  );
};

export default ValidatedSelect;
